import { Building2 } from 'lucide-react'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useProgresoLaboratorios } from '@/modules/multichannel/hooks/useCampanias'
import type { CampaniaFilters } from '@/modules/multichannel/types/campania'

interface ProgresoLaboratoriosProps {
  filters?: CampaniaFilters
}

/** Fallback: color por porcentaje si el lab no tiene color propio */
function getFallbackColor(porcentaje: number): { nombre: string; tono: string } {
  if (porcentaje >= 80) return { nombre: 'emerald', tono: '500' }
  if (porcentaje >= 60) return { nombre: 'teal', tono: '500' }
  if (porcentaje >= 40) return { nombre: 'amber', tono: '500' }
  return { nombre: 'rose', tono: '500' }
}

export function ProgresoLaboratorios({ filters }: ProgresoLaboratoriosProps) {
  const { data: progreso, isLoading } = useProgresoLaboratorios(filters)

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
      style={{ background: 'linear-gradient(to right, #57a6d810, transparent 60%)' }}
    >
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center gap-2 mb-1">
          <div className="rounded-full p-1.5" style={{ backgroundColor: '#57a6d815' }}>
            <Building2 className="h-4 w-4" style={{ color: '#57a6d8' }} />
          </div>
          <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Progreso por Laboratorio</span>
        </div>
      </div>
      <div className="px-4 pb-4">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : !progreso || progreso.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Sin datos de progreso
          </p>
        ) : (
          <div className="space-y-3 max-h-[320px] overflow-y-auto">
            {progreso.map((lab) => {
              const color = lab.color_nombre
                ? { nombre: lab.color_nombre, tono: lab.color_tono || '500' }
                : getFallbackColor(lab.porcentaje)
              return (
                <div key={lab.marca} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-2.5 h-2.5 rounded-sm shrink-0 bg-${color.nombre}-${color.tono}`} />
                      <span className="font-medium truncate">{lab.marca}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {lab.enviadas}/{lab.total}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-foreground shrink-0">{lab.porcentaje}%</span>
                  </div>
                  <div className={`h-2 w-full rounded-full bg-${color.nombre}-${color.tono}/10`}>
                    <div
                      className={`h-2 rounded-full transition-all bg-${color.nombre}-${color.tono}/50`}
                      style={{ width: `${lab.porcentaje}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
